import type { core, ZodType } from 'zod';

export type FieldErrors = Readonly<Record<string, string>>;

export type ValidationResult<T> =
  { readonly ok: true; readonly data: T } | { readonly ok: false; readonly errors: FieldErrors };

/** Validates form input with a shared contract schema; first message per field path wins. */
export function validate<T>(schema: ZodType<T>, input: unknown): ValidationResult<T> {
  const parsed = schema.safeParse(input);
  if (parsed.success) {
    return { ok: true, data: parsed.data };
  }
  const errors: Record<string, string> = {};
  for (const issue of parsed.error.issues) {
    const key = issue.path.map(String).join('.') || 'form';
    errors[key] ??= friendlyMessage(issue);
  }
  return { ok: false, errors };
}

/** zod's built-in messages ("Too small: expected string…") are developer wording; schema-specific messages are kept. */
const ZOD_DEFAULT_MESSAGE = /^(Too small|Too big|Invalid)\b/;

export function friendlyMessage(issue: core.$ZodIssue): string {
  if (!ZOD_DEFAULT_MESSAGE.test(issue.message)) return issue.message;
  if (issue.code === 'too_small') {
    if (issue.origin === 'string') {
      return Number(issue.minimum) <= 1
        ? 'This field is required.'
        : `Enter at least ${String(issue.minimum)} characters.`;
    }
    if (issue.origin === 'array' || issue.origin === 'set') {
      return `Add at least ${String(issue.minimum)}.`;
    }
    return `Enter a value of at least ${String(issue.minimum)}.`;
  }
  if (issue.code === 'too_big') {
    if (issue.origin === 'string') return `Use at most ${String(issue.maximum)} characters.`;
    if (issue.origin === 'array' || issue.origin === 'set') {
      return `Add no more than ${String(issue.maximum)}.`;
    }
    return `Enter a value of at most ${String(issue.maximum)}.`;
  }
  if (issue.code === 'invalid_type') return 'This field is required.';
  if (issue.code === 'invalid_value') return 'Choose one of the listed options.';
  if (issue.code === 'invalid_format' && issue.format === 'date') return 'Choose a date.';
  return 'Enter a valid value.';
}

/** Optional text input: blank becomes `undefined` so optional schema fields stay absent. */
export function blankToUndefined(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

export function blankToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}
